import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { clamp, fmt } from '@/util';
import { ChevronLeft, ChevronRight } from '@teo/components/icons';

const CLICK_TOL = 3;
const SNAP = 0.1;
const MIN_SPAN = 0.2;
const EPS = 0.05;
const MAX_TICKS = 10;
const THUMB_SIZE = 32;
const HANDLE_W = 8;

type SubtitleCue = {
  id?: string;
  start_ms: number;
  end_ms: number;
  text: string;
};

type TimelineUpdate = {
  _origIndex: number;
  patch: {
    start_ms: number;
    end_ms: number;
  };
};

type SubtitleStripTimelineProps = {
  durationSec: number;
  subtitles: SubtitleCue[];
  currentTime?: number;
  onSeek?: (time: number) => void;
  onUpdateSubtitle?: (index: number, startMs: number, endMs: number) => void;
  onUpdateMany?: (updates: TimelineUpdate[]) => void;
  activeSubtitleIndex?: number | null;
  setActiveSubtitleIndex?: (index: number | null) => void;
  isPlaying?: boolean;
  readOnly?: boolean;
  className?: string;
};

const safeInside = (start: number, end: number) => {
  const pad = Math.max(0.05, SNAP);
  let t = start + pad;

  if (!Number.isFinite(t) || t >= end) {
    t = (start + end) / 2;
  }

  return clamp(t, start + 1e-3, Math.max(start + 1e-3, end - 1e-3));
};

function normalizeText(text: string) {
  return String(text || '')
    .replace(/\s+/g, ' ')
    .trim();
}

export function SubtitleStripTimeline({
  durationSec,
  subtitles,
  currentTime = 0,
  onSeek,
  onUpdateSubtitle,
  onUpdateMany,
  activeSubtitleIndex,
  setActiveSubtitleIndex,
  isPlaying = false,
  readOnly = false,
  className = '',
}: SubtitleStripTimelineProps) {
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);

  const [thumbLeft, setThumbLeft] = useState(0);
  const [drag, setDrag] = useState<any>(null);

  const syncFromScroll = useCallback(() => {
    const viewport = viewportRef.current;
    const track = trackRef.current;

    if (!viewport || !track) return;

    const maxScroll = viewport.scrollWidth - viewport.clientWidth;
    const ratio = maxScroll > 0 ? viewport.scrollLeft / maxScroll : 0;
    const maxLeft = Math.max(0, track.clientWidth - THUMB_SIZE);

    setThumbLeft(ratio * maxLeft);
  }, []);

  const scrollToSubtitleIndex = useCallback(
    (subtitleIndex: number, behavior: ScrollBehavior = 'smooth') => {
      const viewport = viewportRef.current;
      if (!viewport || subtitleIndex == null) return;

      requestAnimationFrame(() => {
        const nextViewport = viewportRef.current;
        if (!nextViewport) return;

        const el = nextViewport.querySelector(
          `[data-subtitle-cue-index="${subtitleIndex}"]`
        ) as HTMLElement | null;

        if (el) {
          const viewportRect = nextViewport.getBoundingClientRect();
          const elRect = el.getBoundingClientRect();

          const elCenter =
            elRect.left -
            viewportRect.left +
            nextViewport.scrollLeft +
            elRect.width / 2;

          nextViewport.scrollTo({
            left: Math.max(0, elCenter - nextViewport.clientWidth / 2),
            behavior,
          });

          return;
        }

        const container = nextViewport.querySelector(
          '[data-subtitle-strip-container]'
        ) as HTMLDivElement | null;

        const cue = subtitles?.[subtitleIndex - 1];

        if (!container || !cue) return;

        const safeDur = durationSec > 0 ? durationSec : 1;
        const startSec = clamp((cue.start_ms || 0) / 1000, 0, safeDur);
        const targetX = (startSec / safeDur) * container.scrollWidth;

        nextViewport.scrollTo({
          left: Math.max(0, targetX - nextViewport.clientWidth / 2),
          behavior,
        });
      });
    },
    [durationSec, subtitles]
  );

  const onThumbDrag = useCallback(
    (event: MouseEvent) => {
      const track = trackRef.current;
      const viewport = viewportRef.current;

      if (!track || !viewport) return;

      const rect = track.getBoundingClientRect();

      let pos = event.clientX - rect.left - THUMB_SIZE / 2;
      pos = Math.max(0, Math.min(pos, rect.width - THUMB_SIZE));

      const ratio =
        rect.width > THUMB_SIZE ? pos / (rect.width - THUMB_SIZE) : 0;

      viewport.scrollLeft =
        ratio * Math.max(0, viewport.scrollWidth - viewport.clientWidth);

      syncFromScroll();
    },
    [syncFromScroll]
  );

  const stopThumbDrag = useCallback(() => {
    document.removeEventListener('mousemove', onThumbDrag);
    document.removeEventListener('mouseup', stopThumbDrag);
  }, [onThumbDrag]);

  const startThumbDrag = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();

      document.addEventListener('mousemove', onThumbDrag);
      document.addEventListener('mouseup', stopThumbDrag);
    },
    [onThumbDrag, stopThumbDrag]
  );

  useEffect(() => {
    return () => {
      stopThumbDrag();
    };
  }, [stopThumbDrag]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return undefined;

    viewport.addEventListener('scroll', syncFromScroll);
    window.addEventListener('resize', syncFromScroll);

    syncFromScroll();

    return () => {
      viewport.removeEventListener('scroll', syncFromScroll);
      window.removeEventListener('resize', syncFromScroll);
    };
  }, [syncFromScroll, subtitles.length]);

  useEffect(() => {
    if (!isPlaying || drag) return;

    const viewport = viewportRef.current;
    const container = viewport?.querySelector(
      '[data-subtitle-strip-container]'
    ) as HTMLDivElement | null;

    if (!viewport || !container) return;

    const safeDur = durationSec > 0 ? durationSec : 1;
    const ratio = clamp(currentTime, 0, safeDur) / safeDur;
    const playheadX = ratio * container.scrollWidth;

    const left = viewport.scrollLeft;
    const right = left + viewport.clientWidth;
    const padding = 40;

    if (playheadX < left + padding || playheadX > right - padding) {
      viewport.scrollTo({
        left: Math.max(0, playheadX - viewport.clientWidth / 2),
        behavior: 'smooth',
      });
    }
  }, [currentTime, durationSec, isPlaying, drag]);

  useEffect(() => {
    if (activeSubtitleIndex == null || drag) return;

    scrollToSubtitleIndex(activeSubtitleIndex);
  }, [activeSubtitleIndex, drag, scrollToSubtitleIndex]);

  return (
    <div
      className={`relative w-full select-none overflow-hidden rounded-lg bg-grey-01 pt-4 ${className}`.trim()}
    >
      <div
        ref={trackRef}
        className="relative mb-4 mt-2 h-[1px] rounded bg-gray-300"
      >
        <div
          className="absolute top-1/2 flex -translate-y-1/2 cursor-grab flex-row items-center rounded bg-yellow-400 shadow active:cursor-grabbing"
          style={{
            width: `${THUMB_SIZE}px`,
            height: `${THUMB_SIZE}px`,
            transform: `translate(${thumbLeft}px, -50%)`,
          }}
          onMouseDown={startThumbDrag}
        >
          <ChevronLeft className="h-5 w-5" />
          <ChevronRight className="h-5 w-5" />
        </div>
      </div>

      <div
        ref={viewportRef}
        className="subtitle-strip-scrollbarless overflow-x-auto overflow-y-hidden"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          padding: '24px 0 48px',
          overscrollBehavior: 'contain',
        }}
      >
        <style>{`
          .subtitle-strip-scrollbarless::-webkit-scrollbar {
            display: none;
          }
        `}</style>

        <SubtitleStripTimelineContent
          durationSec={durationSec}
          subtitles={subtitles}
          currentTime={currentTime}
          onSeek={onSeek}
          onUpdateSubtitle={onUpdateSubtitle}
          onUpdateMany={onUpdateMany}
          activeSubtitleIndex={activeSubtitleIndex}
          setActiveSubtitleIndex={setActiveSubtitleIndex}
          drag={drag}
          setDrag={setDrag}
          readOnly={readOnly}
        />
      </div>
    </div>
  );
}

function SubtitleStripTimelineContent({
  durationSec,
  subtitles,
  currentTime,
  onSeek,
  onUpdateSubtitle,
  onUpdateMany,
  activeSubtitleIndex,
  setActiveSubtitleIndex,
  drag,
  setDrag,
  readOnly,
}: any) {
  const safeDur = durationSec > 0 ? durationSec : 1;
  const containerRef = useRef<HTMLDivElement | null>(null);

  const segments = useMemo(() => {
    return (Array.isArray(subtitles) ? subtitles : [])
      .map((subtitle: SubtitleCue, index: number) => {
        const startSec = clamp((subtitle.start_ms || 0) / 1000, 0, safeDur);
        const endSec = clamp((subtitle.end_ms || 0) / 1000, startSec, safeDur);

        return {
          _origIndex: index,
          startSec,
          endSec,
          text: subtitle.text || '',
        };
      })
      .sort((a, b) => a.startSec - b.startSec)
      .map((subtitle, index, ordered) => {
        const active =
          (currentTime > subtitle.startSec - EPS &&
            currentTime < subtitle.endSec - 1e-6) ||
          (index === ordered.length - 1 &&
            currentTime >= subtitle.endSec - EPS);

        return {
          ...subtitle,
          idx: index,
          active,
        };
      });
  }, [subtitles, currentTime, safeDur]);

  const getPreviewStartEnd = (seg: any) => {
    if (!drag) {
      return {
        start: seg?.startSec,
        end: seg?.endSec,
      };
    }

    if (seg?.idx === drag.idx) {
      return {
        start: drag.previewStart ?? seg.startSec,
        end: drag.previewEnd ?? seg.endSec,
      };
    }

    if (drag.mode === 'left' && seg?.idx === drag.idx - 1) {
      const boundary = drag.previewStart ?? drag.startSec;

      return {
        start: seg.startSec,
        end: Math.max(seg.startSec + MIN_SPAN, Math.min(boundary, seg.endSec)),
      };
    }

    if (drag.mode === 'right' && seg?.idx === drag.idx + 1) {
      const boundary = drag.previewEnd ?? drag.endSec;

      return {
        start: Math.min(
          seg.endSec - MIN_SPAN,
          Math.max(boundary, seg.startSec)
        ),
        end: seg.endSec,
      };
    }

    return {
      start: seg?.startSec,
      end: seg?.endSec,
    };
  };

  const boundsForMove = (idx: number, span: number) => {
    const prevEnd = idx > 0 ? segments[idx - 1].endSec : 0;
    const nextStart =
      idx < segments.length - 1 ? segments[idx + 1].startSec : safeDur;

    return {
      left: clamp(prevEnd + 1e-6, 0, Math.max(0, safeDur - span)),
      right: clamp(nextStart - span - 1e-6, 0, Math.max(0, safeDur - span)),
    };
  };

  const boundsForLeftEdge = (idx: number) => {
    const cur = segments[idx];
    const prev = segments[idx - 1];

    const min = idx === 0 ? 0 : prev.startSec + MIN_SPAN;
    const max = cur.endSec - MIN_SPAN;

    return {
      min: clamp(min, 0, safeDur),
      max: clamp(max, 0, safeDur),
    };
  };

  const boundsForRightEdge = (idx: number) => {
    const cur = segments[idx];
    const next = segments[idx + 1];

    const min = cur.startSec + MIN_SPAN;
    const max = idx === segments.length - 1 ? safeDur : next.endSec - MIN_SPAN;

    return {
      min: clamp(min, 0, safeDur),
      max: clamp(max, 0, safeDur),
    };
  };

  const selectSegment = (seg: any, start: number, end: number) => {
    setActiveSubtitleIndex?.(seg._origIndex + 1);
    onSeek?.(safeInside(start, end));
  };

  const onPointerDown = (
    event: React.PointerEvent,
    mode: 'move' | 'left' | 'right',
    seg: any
  ) => {
    if (readOnly) return;

    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    event.stopPropagation();
    event.currentTarget.setPointerCapture?.(event.pointerId);

    setDrag({
      mode,
      idx: seg.idx,
      _origIndex: seg._origIndex,
      startSec: seg.startSec,
      endSec: seg.endSec,
      secPerPx: safeDur / rect.width,
      x0: event.clientX,
      y0: event.clientY,
      isDragging: false,
    });
  };

  const onPointerMove = (event: PointerEvent) => {
    if (!drag) return;

    const dxPx = event.clientX - drag.x0;
    const dyPx = event.clientY - drag.y0;
    const moved = Math.hypot(dxPx, dyPx) > CLICK_TOL;
    const dxSec = dxPx * drag.secPerPx;

    let start = drag.startSec;
    let end = drag.endSec;

    if (drag.mode === 'move') {
      const span = Math.max(MIN_SPAN, end - start);
      const { left, right } = boundsForMove(drag.idx, span);
      const ns = clamp(start + dxSec, left, right);

      start = ns;
      end = ns + span;
    } else if (drag.mode === 'left') {
      const { min, max } = boundsForLeftEdge(drag.idx);
      start = clamp(drag.startSec + dxSec, min, max);
    } else if (drag.mode === 'right') {
      const { min, max } = boundsForRightEdge(drag.idx);
      end = clamp(drag.endSec + dxSec, min, max);
    }

    const snap = (x: number) => Math.round(x / SNAP) * SNAP;

    setDrag({
      ...drag,
      isDragging: moved,
      previewStart: snap(start),
      previewEnd: snap(end),
    });
  };

  const commitUpdates = (updates: TimelineUpdate[]) => {
    if (onUpdateMany && updates.length > 1) {
      onUpdateMany(updates);
      return;
    }

    updates.forEach(({ _origIndex, patch }) => {
      onUpdateSubtitle?.(_origIndex, patch.start_ms, patch.end_ms);
    });
  };

  const onPointerUp = () => {
    if (!drag) return;

    const start = drag.previewStart ?? drag.startSec;
    const end = drag.previewEnd ?? drag.endSec;

    if (drag.mode === 'move') {
      commitUpdates([
        {
          _origIndex: drag._origIndex,
          patch: {
            start_ms: Math.round(start * 1000),
            end_ms: Math.round(end * 1000),
          },
        },
      ]);

      setDrag(null);
      return;
    }

    if (drag.mode === 'left') {
      const updates: TimelineUpdate[] = [
        {
          _origIndex: segments[drag.idx]._origIndex,
          patch: {
            start_ms: Math.round(start * 1000),
            end_ms: Math.round(segments[drag.idx].endSec * 1000),
          },
        },
      ];

      if (drag.idx > 0) {
        const prevSeg = segments[drag.idx - 1];

        if (start < prevSeg.endSec - MIN_SPAN) {
          updates.push({
            _origIndex: prevSeg._origIndex,
            patch: {
              start_ms: Math.round(prevSeg.startSec * 1000),
              end_ms: Math.round(start * 1000),
            },
          });
        }
      }

      commitUpdates(updates);
      setDrag(null);
      return;
    }

    if (drag.mode === 'right') {
      const updates: TimelineUpdate[] = [
        {
          _origIndex: segments[drag.idx]._origIndex,
          patch: {
            start_ms: Math.round(segments[drag.idx].startSec * 1000),
            end_ms: Math.round(end * 1000),
          },
        },
      ];

      if (drag.idx < segments.length - 1) {
        const nextSeg = segments[drag.idx + 1];

        if (end > nextSeg.startSec + MIN_SPAN) {
          updates.push({
            _origIndex: nextSeg._origIndex,
            patch: {
              start_ms: Math.round(end * 1000),
              end_ms: Math.round(nextSeg.endSec * 1000),
            },
          });
        }
      }

      commitUpdates(updates);
      setDrag(null);
    }
  };

  useEffect(() => {
    if (!drag) return undefined;

    const move = (event: PointerEvent) => onPointerMove(event);
    const up = () => onPointerUp();

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);

    const previousUserSelect = document.body.style.userSelect;
    document.body.style.userSelect = 'none';

    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      document.body.style.userSelect = previousUserSelect;
    };
  }, [drag]);

  const onBarClick = (event: React.MouseEvent) => {
    if (drag) return;

    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const px = clamp(event.clientX - rect.left, 0, rect.width);
    const sec = (px / rect.width) * safeDur;

    onSeek?.(sec);
  };

  const ticks = useMemo(() => {
    if (!Number.isFinite(safeDur) || safeDur <= 0) return [0];

    const rawStep = safeDur / MAX_TICKS;
    const pow10 = Math.pow(10, Math.floor(Math.log10(rawStep)));
    const candidates = [1, 2, 5].map((m) => m * pow10);

    let interval = candidates[candidates.length - 1];

    for (const step of candidates) {
      if (rawStep <= step) {
        interval = step;
        break;
      }
    }

    const arr = [];

    for (let tick = 0; tick <= safeDur + 1e-6; tick += interval) {
      arr.push(clamp(tick, 0, safeDur));
    }

    if (arr[arr.length - 1] !== safeDur) {
      arr.push(safeDur);
    }

    return Array.from(new Set(arr.map((x) => +x.toFixed(3))));
  }, [safeDur]);

  let dragTooltip = null;

  if (drag && drag.isDragging) {
    const seg = segments[drag.idx];
    const pStart = drag.previewStart ?? seg.startSec;
    const pEnd = drag.previewEnd ?? seg.endSec;

    let time = null;

    if (drag.mode === 'move' || drag.mode === 'left') time = pStart;
    if (drag.mode === 'right') time = pEnd;

    if (time != null) {
      const leftPct = (time / safeDur) * 100;

      dragTooltip = (
        <div
          className="pointer-events-none absolute -top-5 select-none rounded-sm bg-black/80 px-1.5 py-[1px] text-[11px] font-semibold text-white"
          style={{
            left: `calc(${leftPct}% - 20px)`,
            minWidth: '40px',
            textAlign: 'center',
          }}
        >
          {fmt(time)}
        </div>
      );
    }
  }

  return (
    <div
      data-subtitle-strip-container
      style={{
        position: 'relative',
        width: `${Math.max(1, segments.length) * 260}px`,
        minWidth: '100%',
      }}
    >
      <div className="flex justify-between px-2 text-xs text-grey-05">
        {ticks.map((tick, index) => (
          <React.Fragment key={tick}>
            {index !== 0 && <span>•</span>}
            {index !== 0 && <span>•</span>}
            <span>{fmt(tick)}</span>
          </React.Fragment>
        ))}
      </div>

      <div
        ref={containerRef}
        className="relative mt-3 h-10 w-full cursor-pointer select-none rounded-md bg-white/60 px-1 py-1 shadow-inner outline-none focus:outline-none"
        style={{
          caretColor: 'transparent',
          userSelect: 'none',
        }}
        tabIndex={-1}
        onMouseDown={(event) => event.preventDefault()}
        onClick={onBarClick}
      >
        {segments.map((seg) => {
          const preview = getPreviewStartEnd(seg);
          const leftPct = (preview.start / safeDur) * 100;
          const widthPct = ((preview.end - preview.start) / safeDur) * 100;

          const isSelected =
            activeSubtitleIndex === seg._origIndex + 1 ||
            seg.active ||
            (drag && drag.idx === seg.idx);

          return (
            <button
              key={seg._origIndex}
              type="button"
              data-subtitle-cue-index={seg._origIndex + 1}
              className={`absolute top-1 h-8 overflow-hidden rounded-md px-2 text-left text-xs transition ${
                isSelected
                  ? 'border border-secondary-04 bg-grey-06 text-white'
                  : 'bg-grey-03 text-grey-09'
              }`}
              style={{
                left: `${leftPct}%`,
                width: `${widthPct}%`,
              }}
              onClick={(event) => {
                event.stopPropagation();
                selectSegment(seg, preview.start, preview.end);
              }}
              title={seg.text}
            >
              <span className="block truncate">{normalizeText(seg.text)}</span>

              {!readOnly && (
                <>
                  <span
                    className="absolute inset-0 cursor-grab active:cursor-grabbing"
                    style={{
                      userSelect: 'none',
                      caretColor: 'transparent',
                    }}
                    onPointerDown={(event) => onPointerDown(event, 'move', seg)}
                  />

                  <span
                    className="absolute left-0 top-0 h-full cursor-ew-resize"
                    style={{
                      width: HANDLE_W,
                      userSelect: 'none',
                      caretColor: 'transparent',
                    }}
                    onPointerDown={(event) => onPointerDown(event, 'left', seg)}
                    title={
                      seg.idx === 0 ? 'Adjust start' : 'Adjust shared boundary'
                    }
                  />

                  <span
                    className="absolute right-0 top-0 h-full cursor-ew-resize"
                    style={{
                      width: HANDLE_W,
                      userSelect: 'none',
                      caretColor: 'transparent',
                    }}
                    onPointerDown={(event) =>
                      onPointerDown(event, 'right', seg)
                    }
                    title={
                      seg.idx === segments.length - 1
                        ? 'Adjust end'
                        : 'Adjust shared boundary'
                    }
                  />
                </>
              )}
            </button>
          );
        })}

        {dragTooltip}

        <div
          className="absolute top-[-38px] z-[20] h-[116px] w-[2px] bg-primary-04"
          style={{
            left: `${(clamp(currentTime, 0, safeDur) / safeDur) * 100}%`,
          }}
        >
          <span
            className="absolute -left-1"
            style={{
              width: 0,
              height: 0,
              borderStyle: 'solid',
              borderWidth: '10px 5px 0 5px',
              borderColor: '#FDCA33 transparent transparent transparent',
            }}
          />

          <span
            className="absolute -left-1"
            style={{
              bottom: 0,
              width: 0,
              height: 0,
              borderStyle: 'solid',
              borderWidth: '0 5px 10px 5px',
              borderColor: 'transparent transparent #FDCA33 transparent',
            }}
          />

          <span className="absolute -left-[3px] top-[43px] h-8 w-2 rounded-lg bg-primary-04" />
        </div>
      </div>
    </div>
  );
}
